<template>
  <div class="vacancy-list">
    <Heading
      v-if="blok.showTitle"
      :blok="headingBlok"
    />

    <div
      v-if="
        blok.pilotsDisclaimer &&
        filterStore.teams.length === 1 &&
        filterStore.teams[0] === 'pilots' &&
        vacancies.length === 0
      "
      class="text"
      v-dompurify-html="renderRichText(blok.pilotsDisclaimer)"
    />
    <VacancyCard
      v-for="vacancy in vacancies"
      :key="vacancy.full_slug"
      :blok="{ ...vacancy, showImage: blok.showImage }"
    />

    <NoVacancies
      v-if="blok.showNoVacancies && !vacancies.length"
      :blok="noVacancyBlok"
    />
    <Link
      v-if="blok.showButton && blok.buttonRedirect"
      :blok="blok.buttonRedirect"
    >
      <GeneralElementsButton
        :label="blok.buttonLabel ?? ''"
        class="blue arrow-right"
      />
    </Link>

    <GeneralElementsBackToTop v-if="vacancies.length > 8" />
  </div>
</template>

<script setup lang="ts">
  import type IHeading from '~/types/Storyblok/General/Heading'
  import type ITeam from '~/types/Storyblok/Vacancy/Team'
  import type IVacancyCard from '~/types/Storyblok/Vacancy/VacancyCard'
  import type IVacancyList from '~/types/Storyblok/Vacancy/VacancyList'
  import { useAppStore, useFilterStore, useTeamsStore } from '~/stores'
  import { useFetchFilteredVacancies } from '~/composables/useFetchFilteredVacancies'
  import Link from '~/storyblok/Generic/Link.vue'
  import renderRichText from '~/utils/renderSbRichText'

  const props = defineProps<{
    blok: IVacancyList
  }>()

  const { locale } = useI18n()
  const appStore = useAppStore()
  const teamsStore = useTeamsStore()
  const filterStore = useFilterStore()
  const { fetchIndexVacancies, fetchTeamVacancies, fetchInternshipVacancies, fetchFilteredVacancies } =
    useFetchFilteredVacancies()

  const headingBlok: IHeading = {
    type: 'h1',
    text: props.blok.title ?? '',
    showUnderline: true
  }

  const noVacancyBlok = computed<{ text: string }>(() => ({
    text:
      typeof props.blok.noVacanciesText === 'string'
        ? props.blok.noVacanciesText
        : props.blok.noVacanciesText?.text ?? ''
  }))
  const vacancies = ref<IVacancyCard[]>([])

  switch (props.blok.type) {
    case 'index':
      fetchIndexVacancies(locale.value).then((response) => {
        vacancies.value = response
      })
      break
    case 'team':
      if (!teamsStore.teams.length) {
        await teamsStore.fetchTeams(locale.value)
        teamsStore.fetchVacancyCount(locale.value)
      }

      teamsStore.activeTeam = teamsStore.teams.find((team: ITeam) => team.code === appStore.slug)?.code ?? ''

      fetchTeamVacancies(locale.value, teamsStore.activeTeam).then((response) => {
        vacancies.value = response
      })

      break
    case 'internship':
      fetchInternshipVacancies(locale.value).then((response) => {
        vacancies.value = response
      })
      break
    default:
      fetchFilteredVacancies(locale.value, filterStore.teams, filterStore.search).then((response) => {
        vacancies.value = response
      })

      filterStore.$subscribe((_mutation: any, state: any) => {
        fetchFilteredVacancies(locale.value, state.teams, state.search).then((response) => {
          vacancies.value = response
        })
      })
      break
  }
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .vacancy-list {
    flex: 1;
    padding: 0 rem(20) rem(40);

    .card {
      &:not(:last-child) {
        margin-bottom: rem(24);
      }
    }

    .button {
      margin: rem(40) auto;

      @include tablet-up {
        margin: rem(64) auto;
      }
    }
  }

  .text {
    font-size: rem(16);
    line-height: rem(32);
  }
</style>
